import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import AssistantIntroModal, { introAlreadySeen } from './AssistantIntroModal';
import { useAppRoles } from '../../../helpers/useAppRoles';
import { useStyles } from './AssistantChat.styles';
import RobotFace, { RobotMood } from './RobotFace';
import { ASSISTANT_CHAT, ASSISTANT_ENABLED } from '../../../graphql/assistant.queries';
import { useI18n } from '../../../i18n';
import { LANGUAGE_LABELS } from '../../../i18n/types';
import { isSpanishOnlyLocation } from '../../../i18n/spanishOnly';

interface IChatQr {
  value: string;
  caption: string;
}

interface IChatMessage {
  from: 'me' | 'assistant' | 'failed';
  text: string;
  /** A lunch QR drawn under the reply. */
  qr?: IChatQr | null;
}

/** A button under the last reply, loaded by the server with real data. */
interface IChatOption {
  label: string;
  action: string;
  /** Only admins see these; painted apart. */
  admin?: boolean;
}

/** Buttons shown before "Más". */
const VISIBLE_OPTIONS = 4;
const VISIBLE_ADMIN_OPTIONS = 3;

interface IChatState {
  conversationId: string | null;
  messages: IChatMessage[];
  options: IChatOption[];
}

/** Catalog keys of the starters shown if the server greeting does not arrive. */
const SUGGESTIONS = [
  'assistant.suggest.bookTomorrow',
  'assistant.suggest.myReservations',
  'assistant.suggest.attendance',
  'assistant.suggest.leftovers',
];

/**
 * Session storage, not component state alone: the language switch remounts
 * everything under I18nProvider, and a reload should not lose the chat either.
 */
const STORAGE_KEY = 'deskbooking.assistant';
/** Open or closed survives the remount a language switch causes. */
const OPEN_KEY = 'deskbooking.assistant.open';
const GREETED_KEY = 'deskbooking.assistant.greeted';

const EMPTY: IChatState = { conversationId: null, messages: [], options: [] };

/** Replies come back in milliseconds; a short pause reads as thought, not lag. */
const MIN_THINKING_MS = 450;
const pause = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const loadState = (): IChatState => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const stored = raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
    return { ...stored, options: Array.isArray(stored.options) ? stored.options : [] };
  } catch (e) {
    return EMPTY;
  }
};

const storeState = (state: IChatState): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // Storage unavailable: the chat still works for this visit.
  }
};

const alreadyGreeted = (): boolean => {
  try {
    return sessionStorage.getItem(GREETED_KEY) === '1';
  } catch (e) {
    return true;
  }
};

const markGreeted = (): void => {
  try {
    sessionStorage.setItem(GREETED_KEY, '1');
  } catch (e) {
    // Without storage the greeting may show again; harmless.
  }
};

const loadOpen = (): boolean => {
  try {
    return sessionStorage.getItem(OPEN_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const storeOpen = (open: boolean): void => {
  try {
    sessionStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch (e) {
    // Without storage the panel closes on a language switch; harmless.
  }
};

const ShieldIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
);

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Reveals a reply as if it were being typed. Fast on purpose — a long answer
 * still lands in about a second — and skipped entirely for reduced motion.
 */
const TypedText: React.FC<{ text: string; caretClass: string; onDone: () => void }> = ({
  text,
  caretClass,
  onDone,
}) => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const step = Math.max(1, Math.ceil(text.length / 60));
    const timer = window.setInterval(() => {
      setShown((n) => {
        const next = Math.min(text.length, n + step);
        if (next >= text.length) window.clearInterval(timer);
        return next;
      });
    }, 16);
    return () => window.clearInterval(timer);
  }, [text]);
  useEffect(() => {
    if (shown >= text.length) onDone();
  }, [shown, text.length, onDone]);
  return (
    <>
      {text.slice(0, shown)}
      {shown < text.length && <span className={caretClass} />}
    </>
  );
};

const CloseIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const NewChatIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 12a8 8 0 11-2.34-5.66M20 4v4.5h-4.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 12l16-8-6 16-2.5-6.5L4 12z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Floating chat with the booking assistant. It books, cancels and edits
 * reservations through the server, which acts as the signed-in person only;
 * this component just carries the messages and refreshes the screens when a
 * reservation changed.
 */
const AssistantChat: React.FC = () => {
  const classes = useStyles();
  const { t, language, setLanguage } = useI18n();
  const location = useLocation();
  const client = useApolloClient();
  const { isSuperAdmin, isDeskbookingAdmin, isLunchAdmin } = useAppRoles();
  const [open, setOpenState] = useState(loadOpen);
  const setOpen = (value: boolean) => {
    storeOpen(value);
    setOpenState(value);
  };
  const [expanded, setExpanded] = useState({ normal: false, admin: false });
  const [bigQr, setBigQr] = useState<IChatQr | null>(null);
  const [intro, setIntro] = useState(false);
  // A language the chat switched to; applied once the reply is stored, since
  // switching remounts everything below the provider.
  const [switchTo, setSwitchTo] = useState<'es' | 'en' | null>(null);
  const [hover, setHover] = useState(false);
  const [greeting, setGreeting] = useState(false);
  const [draft, setDraft] = useState('');
  const [state, setState] = useState<IChatState>(loadState);
  // Index of the reply being typed out; restored history appears at once.
  const [typing, setTyping] = useState<number | null>(null);
  const [mood, setMood] = useState<RobotMood>('idle');
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { data } = useQuery<{ assistantEnabled: boolean }>(ASSISTANT_ENABLED, {
    fetchPolicy: 'cache-first',
  });
  const [sendMessage, { loading }] = useMutation(ASSISTANT_CHAT);
  const enabled = Boolean(data?.assistantEnabled) && !isSpanishOnlyLocation(location);

  useEffect(() => storeState(state), [state]);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  });

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Say hello once per session, a moment after the app settles.
  useEffect(() => {
    if (!enabled || open || alreadyGreeted()) return undefined;
    const show = window.setTimeout(() => {
      setGreeting(true);
      markGreeted();
    }, 2500);
    const hide = window.setTimeout(() => setGreeting(false), 10500);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [enabled, open]);

  useEffect(() => {
    if (loading) setMood('thinking');
  }, [loading]);

  // What the chat can do now, once per person, a moment after the app loads.
  useEffect(() => {
    if (!enabled || introAlreadySeen()) return undefined;
    const timer = window.setTimeout(() => setIntro(true), 1200);
    return () => window.clearTimeout(timer);
  }, [enabled]);

  useEffect(() => {
    if (!switchTo) return;
    storeState(state);
    storeOpen(open);
    setSwitchTo(null);
    if (switchTo !== language) setLanguage(switchTo);
  }, [switchTo, state, open, language, setLanguage]);

  // Asset Control is a separate, Spanish-only tool used at the scanner.
  if (!enabled) return null;

  const openChat = () => {
    setGreeting(false);
    setOpen(true);
    // A new chat opens with the server's greeting: next reservation, the
    // usual booking in one tap, leftovers if there are any.
    if (!state.messages.length) send('', 'start');
  };

  /**
   * Sends what was typed, or the option tapped (its label shows as the
   * person's message, its action tells the server exactly what was chosen).
   * With an action and no text it is the silent greeting when the chat opens.
   */
  const send = async (text: string, action?: string) => {
    const message = text.trim();
    if ((!message && !action) || loading) return;
    setDraft('');
    setTyping(null);
    setState((s) => ({
      ...s,
      options: [],
      messages: message ? [...s.messages, { from: 'me', text: message }] : s.messages,
    }));
    const shown = state.messages.length + (message ? 1 : 0);
    try {
      const [result] = await Promise.all([
        sendMessage({
          variables: { conversationId: state.conversationId, message, action: action ?? null, language },
        }),
        pause(MIN_THINKING_MS),
      ]);
      const reply = result.data?.assistantChat;
      if (!reply) throw new Error('empty reply');
      setExpanded({ normal: false, admin: false });
      setState((s) => ({
        conversationId: reply.conversationId,
        messages: reply.reply
          ? [...s.messages, { from: 'assistant', text: reply.reply, qr: reply.qr ? { value: reply.qr.value, caption: reply.qr.caption } : null }]
          : s.messages,
        options: (reply.options ?? []).map((o: IChatOption) => ({ label: o.label, action: o.action, admin: Boolean(o.admin) })),
      }));
      if (reply.language === 'es' || reply.language === 'en') setSwitchTo(reply.language);
      // Sending is locked while waiting, so the reply lands right after the
      // message added above.
      if (reply.reply && !prefersReducedMotion()) setTyping(shown);
      setMood(reply.changed ? 'happy' : 'idle');
      if (reply.changed) {
        // The map, "My reservations" and the lunch QR all read from the cache.
        client.refetchQueries({ include: 'active' }).catch(() => undefined);
      }
    } catch (e) {
      setMood('idle');
      setState((s) => ({
        ...s,
        messages: [...s.messages, { from: 'failed', text: t('assistant.error') }],
      }));
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send(draft);
    }
  };

  const restart = async () => {
    setDraft('');
    setTyping(null);
    setMood('idle');
    inputRef.current?.focus();
    if (loading) return;
    setState(EMPTY);
    try {
      const [result] = await Promise.all([
        sendMessage({ variables: { conversationId: null, message: '', action: 'start', language } }),
        pause(MIN_THINKING_MS),
      ]);
      const reply = result.data?.assistantChat;
      if (!reply) return;
      setExpanded({ normal: false, admin: false });
      setState({
        conversationId: reply.conversationId,
        messages: reply.reply ? [{ from: 'assistant', text: reply.reply }] : [],
        options: (reply.options ?? []).map((o: IChatOption) => ({ label: o.label, action: o.action, admin: Boolean(o.admin) })),
      });
      if (!prefersReducedMotion()) setTyping(0);
    } catch (e) {
      // The static welcome and starters stay on screen.
    }
  };

  const introModal = (
    <AssistantIntroModal
      open={intro}
      isAdmin={isSuperAdmin || isDeskbookingAdmin || isLunchAdmin}
      onClose={() => setIntro(false)}
      onTry={() => {
        setIntro(false);
        openChat();
      }}
    />
  );

  if (!open) {
    return (
      <>
      {introModal}
      <div className={classes.dock}>
        <button
          type="button"
          className={classes.launcher}
          onClick={openChat}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          aria-label={t('assistant.open')}
          title={t('assistant.open')}
        >
          <span className={classes.launcherFace}>
            <RobotFace size={46} mood={hover ? 'happy' : 'idle'} />
          </span>
        </button>
        {greeting && (
          <button type="button" className={classes.greeting} onClick={openChat}>
            {t('assistant.greeting')}
          </button>
        )}
      </div>
      </>
    );
  }

  const normalOptions = state.options.filter((o) => !o.admin);
  const adminOptions = state.options.filter((o) => o.admin);
  const shownNormal = expanded.normal ? normalOptions : normalOptions.slice(0, VISIBLE_OPTIONS);
  const shownAdmin = expanded.admin ? adminOptions : adminOptions.slice(0, VISIBLE_ADMIN_OPTIONS);
  const switchLanguage = (next: 'es' | 'en') => {
    if (next === language || loading) return;
    // The chat answers in the new language, then the app follows.
    send('', `lang:${next}`);
  };

  const lastReply = [...state.messages].reverse().find((m) => m.from !== 'me');
  // Until the server has said something, the static welcome and starters stand in.
  const greeted = state.messages.some((m) => m.from === 'assistant');

  return (
    <section className={classes.panel} role="dialog" aria-labelledby="assistant-title">
      <header className={classes.header}>
        <span className={classes.avatar}>
          <RobotFace size={34} mood={mood} />
        </span>
        <div className={classes.headerText}>
          <div className={classes.titleRow}>
            <h2 id="assistant-title" className={classes.title}>
              {t('assistant.title')}
            </h2>
          </div>
          <p className={classes.subtitle}>
            <span className={classes.onlineDot} aria-hidden="true" />
            {loading ? t('assistant.thinking') : t('assistant.subtitle')}
          </p>
        </div>
        <div className={classes.langToggle} role="group" aria-label={t('assistant.languageToggle')}>
          {(['es', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              lang={l}
              className={`${classes.langOption} ${language === l ? classes.langOptionActive : ''}`}
              aria-pressed={language === l}
              onClick={() => switchLanguage(l)}
              title={LANGUAGE_LABELS[l].name}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={classes.headerButton}
          onClick={restart}
          aria-label={t('assistant.newChat')}
          title={t('assistant.newChat')}
        >
          <NewChatIcon />
        </button>
        <button
          type="button"
          className={classes.headerButton}
          onClick={() => setOpen(false)}
          aria-label={t('assistant.close')}
          title={t('assistant.close')}
        >
          <CloseIcon />
        </button>
      </header>

      <div className={classes.log} ref={logRef}>
        {!greeted && !loading && (
          <div className={classes.row}>
            <span className={classes.miniAvatar}>
              <RobotFace size={22} />
            </span>
            <div className={`${classes.bubble} ${classes.fromAssistant}`}>
              {t('assistant.welcome')}
            </div>
          </div>
        )}
        {!greeted && !loading && (
          <div className={classes.suggestions}>
            {SUGGESTIONS.map((key) => (
              <button
                key={key}
                type="button"
                className={classes.suggestion}
                onClick={() => send(t(key))}
              >
                {t(key)}
              </button>
            ))}
          </div>
        )}
        {state.messages.map((m, i) =>
          m.from === 'me' ? (
            <div key={i} className={`${classes.row} ${classes.rowMine}`}>
              <div className={`${classes.bubble} ${classes.fromMe}`}>
                <span className={classes.srOnly}>{t('assistant.you')}: </span>
                {m.text}
              </div>
            </div>
          ) : (
            <div key={i} className={classes.row}>
              <span className={classes.miniAvatar}>
                <RobotFace size={22} />
              </span>
              <div
                className={`${classes.bubble} ${
                  m.from === 'failed' ? classes.failed : classes.fromAssistant
                }`}
                onClick={() => typing === i && setTyping(null)}
              >
                {typing === i ? (
                  <TypedText
                    text={m.text}
                    caretClass={classes.caret}
                    onDone={() => setTyping((current) => (current === i ? null : current))}
                  />
                ) : (
                  m.text
                )}
              </div>
            </div>
          ),
        )}
        {(() => {
          const withQr = [...state.messages].reverse().find((m) => m.qr);
          if (!withQr?.qr || typing !== null) return null;
          const qr = withQr.qr;
          return (
            <button type="button" className={classes.qrCard} onClick={() => setBigQr(qr)} aria-label={t('assistant.qrEnlarge')}>
              <QRCodeSVG value={qr.value} size={150} includeMargin />
              <p className={classes.qrCaption}>{qr.caption}</p>
            </button>
          );
        })()}
        {!loading && typing === null && normalOptions.length > 0 && (
          <div className={classes.suggestions}>
            {shownNormal.map((o) => (
              <button
                key={o.action + o.label}
                type="button"
                className={classes.suggestion}
                onClick={() => send(o.label, o.action)}
              >
                {o.label}
              </button>
            ))}
            {normalOptions.length > shownNormal.length && (
              <button type="button" className={classes.moreChip} onClick={() => setExpanded((e) => ({ ...e, normal: true }))}>
                {t('assistant.more', { count: normalOptions.length - shownNormal.length })}
              </button>
            )}
          </div>
        )}
        {!loading && typing === null && adminOptions.length > 0 && (
          <div className={classes.adminGroup}>
            <p className={classes.adminLabel}>
              <ShieldIcon />
              {t('assistant.adminLabel')}
            </p>
            <div className={classes.adminChips}>
              {shownAdmin.map((o) => (
                <button
                  key={o.action + o.label}
                  type="button"
                  className={classes.adminChip}
                  onClick={() => send(o.label, o.action)}
                >
                  {o.label}
                </button>
              ))}
              {adminOptions.length > shownAdmin.length && (
                <button type="button" className={classes.moreChip} onClick={() => setExpanded((e) => ({ ...e, admin: true }))}>
                  {t('assistant.more', { count: adminOptions.length - shownAdmin.length })}
                </button>
              )}
            </div>
          </div>
        )}
        {loading && (
          <div className={classes.row}>
            <span className={classes.miniAvatar}>
              <RobotFace size={22} mood="thinking" />
            </span>
            <div className={classes.typing}>
              <span className={classes.dot} />
              <span className={classes.dot} />
              <span className={classes.dot} />
            </div>
          </div>
        )}
      </div>

      {/* Screen readers get each reply whole, not letter by letter. */}
      <div className={classes.srOnly} aria-live="polite" role="status">
        {loading ? t('assistant.thinking') : lastReply?.text ?? ''}
      </div>

      <div className={classes.composer}>
        <textarea
          ref={inputRef}
          className={classes.input}
          rows={1}
          maxLength={1000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('assistant.placeholder')}
          aria-label={t('assistant.placeholder')}
        />
        <button
          type="button"
          className={classes.send}
          onClick={() => send(draft)}
          disabled={loading || !draft.trim()}
          aria-label={t('assistant.send')}
          title={t('assistant.send')}
        >
          <SendIcon />
        </button>
      </div>
      <p className={classes.footnote}>{t('assistant.footnote')}</p>
      {bigQr && (
        <div className={classes.qrOverlay} role="dialog" aria-label={bigQr.caption} onClick={() => setBigQr(null)}>
          <div className={classes.qrBig}>
            <QRCodeSVG value={bigQr.value} size={Math.min(320, window.innerWidth - 80)} includeMargin />
          </div>
          <p className={classes.qrBigCaption}>{bigQr.caption}</p>
          <button type="button" className={classes.qrClose} onClick={() => setBigQr(null)}>
            {t('assistant.close')}
          </button>
        </div>
      )}
      {introModal}
    </section>
  );
};

export default AssistantChat;
